import React, { useState, useMemo, useEffect } from 'react';
import { useTestMasters } from '../../hooks/useTestMasters';
import { FaEdit, FaTrash, FaPlus, FaFileExport } from 'react-icons/fa';
import { exportToCSV } from '../../utils/exportUtils';
import { confirmAlert } from 'react-confirm-alert';
import { toast } from 'react-toastify';
import PaginationControls from './PaginationControls';
import 'react-confirm-alert/src/react-confirm-alert.css';

// Helper function to get badge color based on gender
const getGenderBadgeColor = (gender) => {
  switch (gender?.toLowerCase()) {
    case 'male':
      return 'text-blue-800 bg-blue-100';
    case 'female':
      return 'text-pink-800 bg-pink-100';
    default:
      return 'text-purple-800 bg-purple-100'; // For 'both' or undefined
  }
};

// Helper function to get color for age range
const getAgeRangeColor = (minAge, maxAge) => {
  // Handle undefined or null values
  if (minAge === undefined || minAge === null || maxAge === undefined || maxAge === null) {
    return 'text-gray-600';
  }
  
  const avgAge = (parseInt(minAge) + parseInt(maxAge)) / 2;
  
  if (avgAge < 18) {
    return 'text-green-600 font-medium';
  } else if (avgAge < 40) {
    return 'text-blue-600 font-medium';
  } else if (avgAge < 60) {
    return 'text-amber-600 font-medium';
  } else {
    return 'text-red-600 font-medium';
  }
};

// Helper function to get background color for grouping by biomarker name
const getBiomarkerGroupColor = (currentBiomarker, index, allTests) => {
  // Get all unique biomarker names up to this point
  const uniqueBiomarkers = [];
  for (let i = 0; i <= index; i++) {
    if (!uniqueBiomarkers.includes(allTests[i].biomarker_name)) {
      uniqueBiomarkers.push(allTests[i].biomarker_name);
    }
  }
  
  // Find the index of the current biomarker in the unique biomarkers list
  const uniqueIndex = uniqueBiomarkers.indexOf(currentBiomarker);
  
  // Alternate background based on the unique biomarker index
  return uniqueIndex % 2 === 0 ? 'bg-white' : 'bg-orange-50';
};

const TestMasterList = () => {
  const { 
    loading, 
    openEditModal, 
    deleteTestMaster, 
    openCreateModal,
    filteredTestMasters,
    updateFilters,
    filters,
    allTestMasters
  } = useTestMasters();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [testsPerPage] = useState(10);
  
  // Calculate biomarker and non-biomarker counts
  const { biomarkerCount, nonBiomarkerCount } = useMemo(() => {
    const biomarkers = allTestMasters.filter(test => !test.non_biomarker).length;
    const nonBiomarkers = allTestMasters.filter(test => test.non_biomarker).length;
    return { biomarkerCount: biomarkers, nonBiomarkerCount: nonBiomarkers };
  }, [allTestMasters]);
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredTestMasters.length]);
  
  // Get current tests for pagination
  const { currentTests, totalPages } = useMemo(() => {
    const indexOfLastTest = currentPage * testsPerPage;
    const indexOfFirstTest = indexOfLastTest - testsPerPage;
    const current = filteredTestMasters.slice(indexOfFirstTest, indexOfLastTest);
    const total = Math.ceil(filteredTestMasters.length / testsPerPage);
    return { currentTests: current, totalPages: total };
  }, [filteredTestMasters, currentPage, testsPerPage]);
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  const handleSearchChange = (e) => {
    updateFilters({ search: e.target.value });
  };

  const handleClinicalCategoryChange = (e) => {
    updateFilters({ clinicalDataCategory: e.target.value });
  };

  const handleHealthScoreChange = (e) => {
    updateFilters({ healthScoreCategory: e.target.value });
  };

  const handleNonBiomarkerChange = (e) => {
    updateFilters({ nonBiomarker: e.target.checked });
  };
  
  // Get unique clinical data categories
  const clinicalDataCategories = useMemo(() => {
    const categories = new Set();
    allTestMasters.forEach(test => {
      if (test.clinical_data_category && test.clinical_data_category.profile_name) {
        categories.add(test.clinical_data_category.profile_name);
      }
    });
    return Array.from(categories).sort();
  }, [allTestMasters]);

  // Get unique health score categories
  const healthScoreCategories = useMemo(() => {
    const categories = new Set();
    allTestMasters.forEach(test => {
      if (test.health_score_weightage && Array.isArray(test.health_score_weightage)) {
        test.health_score_weightage.forEach(item => {
          if (item && item.name) {
            categories.add(item.name);
          }
        });
      }
    });
    return Array.from(categories).sort();
  }, [allTestMasters]);

  const handleExport = (dataToExport) => {
    try {
      // Export either all Biomarker Masters or filtered ones based on parameter
      const data = dataToExport === 'all' ? allTestMasters : filteredTestMasters;
      const timestamp = new Date().toISOString().split('T')[0];
      const exportType = dataToExport === 'all' ? 'all' : 'filtered';
      const recordCount = data.length;
      
      // Export the data
      exportToCSV(data, `test-masters-export-${timestamp}`);
      
      // Show success toast
      toast.success(`Successfully exported ${recordCount} ${exportType} Biomarker Masters`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export Biomarker Masters');
    }
  };

  const handleDelete = (testId) => {
    confirmAlert({
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this test master?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => deleteTestMaster(testId)
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // We'll always render the table structure, even when no results are found

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-4 border-b">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <div>
            <h2 className="text-lg font-semibold">Biomarker Masters</h2>
            <p className="text-sm text-gray-500 mt-1">
              {filteredTestMasters?.length || 0} records shown | Biomarkers: {biomarkerCount} | Non-Biomarkers: {nonBiomarkerCount}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              {/* Clinical Data Category Filter */}
              <div className="relative w-full sm:w-48">
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                  value={filters.clinicalDataCategory}
                  onChange={handleClinicalCategoryChange}
                >
                  <option value="">All Categories</option>
                  {clinicalDataCategories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                {filters.clinicalDataCategory && (
                  <button 
                    onClick={() => updateFilters({ clinicalDataCategory: '' })}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    &times;
                  </button>
                )}
              </div>
              
              {/* Health Score Category Filter */}
              <div className="relative w-full sm:w-48">
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                  value={filters.healthScoreCategory}
                  onChange={handleHealthScoreChange}
                >
                  <option value="">All Health Scores</option>
                  {healthScoreCategories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                {filters.healthScoreCategory && (
                  <button 
                    onClick={() => updateFilters({ healthScoreCategory: '' })}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    &times;
                  </button>
                )}
              </div>
              
              {/* Search Box */}
              <div className="relative w-full sm:w-64">
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                  placeholder="Search by name or code"
                  value={filters.search}
                  onChange={handleSearchChange}
                />
                {filters.search && (
                  <button 
                    onClick={() => updateFilters({ search: '' })}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    &times;
                  </button>
                )}
              </div>

              {/* Non-Biomarker Filter */}
              <div className="flex items-center">
                <label className="flex items-center text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={filters.nonBiomarker || false}
                    onChange={handleNonBiomarkerChange}
                    className="w-4 h-4 border border-gray-300 rounded mr-2"
                  />
                  Non-Biomarker Only
                </label>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={openCreateModal}
                className="px-4 py-2 bg-green-100 text-green-600 hover:bg-green-200 rounded-md flex items-center transition-colors duration-200 border border-green-200 whitespace-nowrap"
              >
                <FaPlus className="mr-2" /> Add New
              </button>
              <div className="relative inline-block text-left">
                <button
                  onClick={() => handleExport('all')}
                  className="px-4 py-2 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded-md flex items-center transition-colors duration-200 border border-blue-200 whitespace-nowrap"
                >
                  <FaFileExport className="mr-2" /> Export All
                </button>
              </div>
              {/* {filteredTestMasters.length !== allTestMasters.length && (
                <button
                  onClick={() => handleExport('filtered')}
                  className="px-4 py-2 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded-md flex items-center transition-colors duration-200 border border-blue-200 whitespace-nowrap"
                >
                  <FaFileExport className="mr-2" /> Export Filtered
                </button>
              )} */}
            </div>
          </div>
        </div>
      </div>
      <table className="min-w-full divide-y divide-gray-200 table-fixed">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="w-1/12 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Code
            </th>
            <th scope="col" className="w-3/12 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">
              Biomarker Name ↓
            </th>
            <th scope="col" className="w-2/12 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Gender & Age Range
            </th>
            <th scope="col" className="w-2/12 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center bg-gray-100">
              Normal Range
            </th>
            <th scope="col" className="w-1/12 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
              Unit
            </th>
            <th scope="col" className="w-3/12 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">
              Actions
            </th>
          </tr>
        </thead>
        <tbody key={`tbody-${filters.nonBiomarker}`} className="divide-y divide-gray-200">
          {currentTests && currentTests.length > 0 ? currentTests.map((test, index) => (
            <tr 
              key={`${test._id}`}
              className={`${getBiomarkerGroupColor(test.biomarker_name, index, currentTests)} hover:bg-blue-50 transition-colors duration-150`}
            >
              <td className="px-6 py-4">
                <div className="text-sm font-medium text-gray-900 break-words">{test.test_code}</div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center">
                  {index === 0 || test.biomarker_name !== currentTests[index - 1].biomarker_name ? (
                    <div className="w-2 h-2 rounded-full bg-orange-400 mr-2 flex-shrink-0"></div>
                  ) : (
                    <div className="w-2 h-2 mr-2 flex-shrink-0"></div>
                  )}
                  <div className="text-sm text-gray-900 break-words">{test.biomarker_name}</div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center">
                  <div className="w-20 flex justify-start flex-shrink-0">
                    <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full ${getGenderBadgeColor(test.gender)}`}>
                      {test.gender ? test.gender.toUpperCase() : 'ALL'}
                    </span>
                  </div>
                  <span className={`text-sm ${getAgeRangeColor(test.agerange_min, test.agerange_max)} break-words`}>
                    {test.agerange_min !== null && test.agerange_max !== null ? `${test.agerange_min} - ${test.agerange_max} yrs` : '-'}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4 text-center">
                <div className="text-sm break-words">{test.normal_range_min !== null && test.normal_range_max !== null ? `${test.normal_range_min} - ${test.normal_range_max}` : '-'}</div>
              </td>
              <td className="px-6 py-4 text-center">
                <div className="text-sm text-gray-500 break-words">{test.unit || '-'}</div>
              </td>
              <td className="px-6 py-4 text-center">
                <div className="flex flex-wrap gap-2 justify-center">
                  <button
                    onClick={() => {
                      // Ensure the test has a valid ID before opening the edit modal
                      if (!test._id && !test.id) {
                        console.error('Error: Missing test ID for edit', test);
                        return;
                      }
                      openEditModal(test);
                    }}
                    className="px-3 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md flex items-center transition-colors duration-200 border border-blue-200"
                  >
                    <FaEdit className="mr-1" /> Edit
                  </button>
                  <button
                    onClick={() => {
                      // Get the ID from either _id or id field, and ensure it's not undefined
                      const testId = test._id || test.id;
                      if (!testId) {
                        console.error('Error: Missing test ID for deletion', test);
                        return;
                      }
                      handleDelete(testId);
                    }}
                    className="px-3 py-1 bg-red-50 text-red-600 hover:bg-red-100 rounded-md flex items-center transition-colors duration-200 border border-red-200"
                  >
                    <FaTrash className="mr-1" /> Delete
                  </button>
                </div>
              </td>
            </tr>
          )) : (
            <tr>
              <td colSpan="6" className="px-6 py-10 text-center">
                <p className="text-gray-500">No Biomarker Masters found. Try adjusting your search.</p>
                {filters.search && (
                  <button 
                    onClick={() => updateFilters({ search: '' })}
                    className="mt-2 px-4 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md transition-colors duration-200 border border-blue-200"
                  >
                    Clear Search
                  </button>
                )}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      
      {totalPages > 1 && (
        <div className="mt-4 px-6 py-3 border-t">
          <PaginationControls 
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default TestMasterList;
